import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { useAdminTheme } from '../contexts/AdminThemeContext';
import { useAuth } from '../contexts/AuthContext';
import AdminSidebar from './AdminSidebar';
import UserDetailModal from './UserDetailModal';
import AdminTemplateManager from './AdminTemplateManager';
import AdminEmailTemplateManager from './AdminEmailTemplateManager';
import AdminTemplateDetailsPage from './AdminTemplateDetailsPage';
import AdminNotificationPage from './AdminNotificationPage';
import AdminSettings from './AdminSettings';
import StandaloneDNSChecker from './StandaloneDNSChecker';
import AdminAutomationTemplateManager from './admin/AdminAutomationTemplateManager';
import AdminAutomationControl from './admin/AdminAutomationControl';
import AdminSignupFormManager from './admin/AdminSignupFormManager';
import AdminVideoTutorialManager from './admin/AdminVideoTutorialManager';
import AdminBlogManager from './admin/AdminBlogManager';
import AdminNewsManager from './admin/AdminNewsManager';
import AdminAppOfferBannerManager from './admin/AdminAppOfferBannerManager';
import AdminCampaignsPage from './admin/AdminCampaignsPage';
import AdminAnalyticsDashboard from './admin/AdminAnalyticsDashboard';
import AdminPlanPricing from './admin/AdminPlanPricing';
import AdminRevenuePage from './admin/AdminRevenuePage';
import { AdminCancelledSubscriptionsPage } from './admin/AdminSubscriptionListPlaceholders';
import AdminCurrentSubscriptionsPage from './admin/AdminCurrentSubscriptionsPage';
import AdminDodoTransactionsPage from './admin/AdminDodoTransactionsPage';
import AdminHelpCenterManager from './admin/AdminHelpCenterManager';
import AdminStatusPageManager from './admin/AdminStatusPageManager';
import AdminFeedbackManager from './admin/AdminFeedbackManager';
import AdminContactSubmissionsPage from './admin/AdminContactSubmissionsPage';
import AdminSystemEmails from './AdminSystemEmails';
import AdminNotificationListener from './AdminNotificationListener';
import AdminWorldMap from './admin/AdminWorldMap';
import { resolveCountryToTopoId, getTopoCountryDisplayName } from '../utils/countryGeoResolver';
import { loadOverviewAccurateMetrics } from '../services/adminOverviewMetrics';
import AdminMarketingPage from './admin/AdminMarketingPage';
import AdminApiManager from './admin/AdminApiManager';
import AdminApiActivity from './admin/AdminApiActivity';
import AdminSenderManager from './admin/AdminSenderManager';
import AdminEmailService from './admin/AdminEmailService';
import AdminSecretsManager from './AdminSecretsManager';
import AdminMailAutomationHub from './admin/AdminMailAutomationHub';
import AdminUploads from './admin/AdminUploads';
import AdminFirebaseDatabase from './admin/AdminFirebaseDatabase';


import { AdminService, SystemMetrics, AdminUser, AdminCampaign } from '../services/adminService';
import { isAdminEmail } from '../config/adminConfig';
import { Timestamp, collection, getDocs, query, where, orderBy, limit, onSnapshot, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { sessionService, Session } from '../services/sessionService';
import { apiKeyService, ApiKey } from '../services/apiKeyService';

interface AdminStats extends SystemMetrics {
  recentSignups: number;
  systemHealth: 'healthy' | 'warning' | 'critical';
  revenueToday: number;
  emailsSentThisWeek: number;
}

interface SystemAlert {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp: Timestamp;
  resolved: boolean;
}


const AdminDashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const { page } = useParams<{ page: string }>();
  const { showError, showSuccess } = useNotification();
  const { isDark, toggleTheme } = useAdminTheme();
  const { currentUser } = useAuth();

  // Read active tab from URL parameter (page slug)
  const activeTab = page || 'overview';
  const [searchTerm, setSearchTerm] = useState('');

  // Redirect to overview if invalid page
  useEffect(() => {
    const validPages = [
      'overview', 'users', 'campaigns', 'email-templates', 'signup-forms', 'automation-templates',
      'automation-control', 'automations', 'system-emails', 'dns-checker', 'templates', 'video-tutorials',
      'blog-posts', 'news-center', 'app-offer-banners', 'help-center', 'analytics', 'revenue', 'current-subscription', 'cancelled-subscription', 'transaction', 'notifications', 'plan', 'settings', 'system', 'email', 'status', 'feedback', 'contact-submissions', 'marketing', 'api', 'api-activity', 'sender', 'email-service', 'system-secrets', 'firebase-database', 'uploads'
    ];

    if (page && !validPages.includes(page) && page !== 'uploads') {
      navigate('/admin/control-panel-7x9k2m/overview', { replace: true });
    }
  }, [page, navigate]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isNotificationCenterOpen, setIsNotificationCenterOpen] = useState(false);
  const [alerts] = useState<SystemAlert[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [adminProfilePicture, setAdminProfilePicture] = useState<string>('');
  const [dataError, setDataError] = useState<string | null>(null);
  const [stats, setStats] = useState<AdminStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalCampaigns: 0,
    totalContacts: 0,
    emailsSentToday: 0,
    emailsSentThisMonth: 0,
    systemUptime: 99.9,
    averageResponseTime: 120,

    // User subscription metrics
    paidUsers: 0,
    unpaidUsers: 0,
    freeUsers: 0,
    premiumUsers: 0,

    // Revenue metrics
    revenueThisMonth: 0,
    revenueLastMonth: 0,
    revenueLast3Months: 0,
    revenueLast6Months: 0,
    revenueThisYear: 0,
    monthlyRecurringRevenue: 0,
    averageRevenuePerUser: 0,

    // Growth metrics
    newUsersThisMonth: 0,
    newUsersLastMonth: 0,
    userGrowthRate: 0,
    churnRate: 0,
    retentionRate: 0,

    // Engagement metrics
    averageContactsPerUser: 0,
    averageCampaignsPerUser: 0,
    emailOpenRate: 0,
    emailClickRate: 0,
    unsubscribeRate: 0,

    // System performance
    totalEmailsSent: 0,
    emailsSentLastMonth: 0,
    averageEmailDeliveryTime: 0,
    bounceRate: 0,

    // Business metrics
    conversionRate: 0,
    customerLifetimeValue: 0,
    totalRevenue: 0,
    monthlyGrowthRate: 0,

    // New Comprehensive Metrics
    annualRecurringRevenue: 0,
    netRevenue: 0,
    revenuePerPlan: {
      starter: 0,
      pro: 0,
      business: 0,
      enterprise: 0
    },
    revenueByBillingCycle: {
      monthly: 0,
      yearly: 0
    },
    revenueByPaymentMethod: {
      card: 0,
      paypal: 0,
      stripe: 0
    },
    revenueByGeography: {},

    // Subscription Health
    newSubscriptionsThisMonth: 0,
    cancelledSubscriptionsThisMonth: 0,
    trialToPaidConversionRate: 0,
    renewalRate: 0,
    expansionRevenue: 0,
    contractionRevenue: 0,

    // Forecast
    revenueForecastNext30Days: 0,

    // Invoices
    recentTransactions: [],

    // Custom properties
    recentSignups: 0,
    systemHealth: 'healthy',
    revenueToday: 0,
    emailsSentThisWeek: 0,
  });

  const [users, setUsers] = useState<AdminUser[]>([]);
  const [campaigns, setCampaigns] = useState<AdminCampaign[]>([]);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isMetricsLoading, setIsMetricsLoading] = useState(true);
  const [isUsersLoading, setIsUsersLoading] = useState(true);
  const [userTab, setUserTab] = useState<'active' | 'online' | 'blocked'>('active');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteProgress, setBulkDeleteProgress] = useState<{ current: number; total: number } | null>(null);
  const [blockConfirmation, setBlockConfirmation] = useState<{ user: AdminUser | null, isOpen: boolean }>({ user: null, isOpen: false });
  const [recentUsersPage, setRecentUsersPage] = useState(1);
  const [recentLoginsPage, setRecentLoginsPage] = useState(1);
  const [dailyEmailData, setDailyEmailData] = useState<number[]>([]);
  const [dailyRevenueData, setDailyRevenueData] = useState<number[]>([]);
  const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
  const searchRef = React.useRef<HTMLDivElement>(null);

  // Individual section loading states
  const [isSessionsLoading, setIsSessionsLoading] = useState(true);
  const [isLoginsLoading, setIsLoginsLoading] = useState(true);
  const [isSuspiciousLoading, setIsSuspiciousLoading] = useState(true);
  const [isChurnLoading, setIsChurnLoading] = useState(true);
  const [isHighValueLoading, setIsHighValueLoading] = useState(true);
  const [isGeoLoading, setIsGeoLoading] = useState(true);

  // New monitoring features state
  const [activeSessions, setActiveSessions] = useState<Array<Session & { user?: AdminUser }>>([]);
  const [recentLogins, setRecentLogins] = useState<Array<Session & { user?: AdminUser }>>([]);
  const [suspiciousActivities, setSuspiciousActivities] = useState<Array<{
    userId: string;
    user?: AdminUser;
    type: string;
    description: string;
    severity: 'low' | 'medium' | 'high';
    timestamp: Date;
  }>>([]);
  const [churnRiskUsers, setChurnRiskUsers] = useState<Array<{
    user: AdminUser;
    daysSinceLastLogin: number;
    riskLevel: 'low' | 'medium' | 'high';
  }>>([]);
  const [highValueUsers, setHighValueUsers] = useState<Array<{
    user: AdminUser;
    totalRevenue: number;
    totalCampaigns: number;
    totalContacts: number;
    engagementScore: number;
  }>>([]);
  const [geographicDistribution, setGeographicDistribution] = useState<Map<string, number>>(new Map());

  // Global Search Filtering Logic
  const filteredResults = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return { users: [], campaigns: [], apiKeys: [] };

    const matchedUsers = users.filter(user => {
      const firstName = user.firstName?.toLowerCase() || '';
      const lastName = user.lastName?.toLowerCase() || '';
      const fullName = `${firstName} ${lastName}`.trim();
      const displayName = user.displayName?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      const id = user.id.toLowerCase();
      return fullName.includes(search) || displayName.includes(search) || email.includes(search) || id.includes(search);
    }).slice(0, 5);

    const matchedCampaigns = campaigns.filter(campaign => {
      const name = campaign.name.toLowerCase();
      const id = campaign.id.toLowerCase();
      const userEmail = campaign.userEmail.toLowerCase();
      const workspaceId = campaign.workspaceId?.toLowerCase() || '';
      return name.includes(search) || id.includes(search) || userEmail.includes(search) || workspaceId.includes(search);
    }).slice(0, 5);

    const matchedApiKeys = apiKeys.filter(key => {
      const name = key.name.toLowerCase();
      const id = key.id.toLowerCase();
      const userEmail = users.find(u => u.id === key.userId)?.email?.toLowerCase() || '';
      return name.includes(search) || id.includes(search) || userEmail.includes(search);
    }).slice(0, 5);

    return { users: matchedUsers, campaigns: matchedCampaigns, apiKeys: matchedApiKeys };
  }, [searchTerm, users, campaigns, apiKeys]);

  // Handle outside click and Escape for search dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchDropdownOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsSearchDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Real-time active sessions listener — replaces the getDocs poll.
  // Fires instantly whenever any userSessions doc changes (login, heartbeat, logout).
  // We keep the same 2-minute inactivity window so a user who closes their tab
  // is removed within at most 2 minutes (one missed heartbeat).
  const loadActiveSessions = useCallback((userList: AdminUser[]) => {
    const TWO_MINUTES_AGO_MS = 2 * 60 * 1000;

    const unsubscribe = onSnapshot(
      query(collection(db, 'userSessions'), where('isActive', '==', true), limit(200)),
      (snapshot) => {
        const now = Date.now();
        const twoMinutesAgo = new Date(now - TWO_MINUTES_AGO_MS);
        const oneHourAgo    = new Date(now - 60 * 60 * 1000);

        const active = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() } as Session))
          .filter(session => {
            const lastActivity = session.lastActivity?.toDate?.();
            const createdAt    = session.createdAt?.toDate?.();
            // Primary: heartbeat within the last 2 minutes
            if (lastActivity && lastActivity > twoMinutesAgo) return true;
            // Fallback: brand-new session (created < 1 h ago, not yet hearbeat-updated)
            if (createdAt && createdAt > oneHourAgo && session.isActive) return true;
            return false;
          })
          .sort((a, b) => {
            const aT = a.lastActivity?.toDate?.()?.getTime() ?? a.createdAt?.toDate?.()?.getTime() ?? 0;
            const bT = b.lastActivity?.toDate?.()?.getTime() ?? b.createdAt?.toDate?.()?.getTime() ?? 0;
            return bT - aT;
          })
          .slice(0, 50);

        const enriched = active.map(session => ({
          ...session,
          user: userList.find(u => u.id === session.userId),
        }));

        setActiveSessions(enriched);
        setIsSessionsLoading(false);
      },
      (error) => {
        console.error('Active sessions listener error:', error);
        setIsSessionsLoading(false);
      }
    );

    return unsubscribe;
  }, []);

  const handleSearchResultClick = (type: 'user' | 'campaign' | 'api', item: any) => {
    setIsSearchDropdownOpen(false);
    setSearchTerm('');

    if (type === 'user') {
      setSelectedUser(item);
      setIsUserModalOpen(true);
      // Optional: switch to users tab if not already there, but modal is global
      if (activeTab !== 'users') navigate('/admin/control-panel-7x9k2m/users');
    } else if (type === 'campaign') {
      navigate('/admin/control-panel-7x9k2m/campaigns');
    } else if (type === 'api') {
      navigate('/admin/control-panel-7x9k2m/api');
    }
  };

  // Enrich users with IP addresses from sessions if deviceInfo is missing
  const enrichUsersWithSessionIPs = useCallback(async (userList: AdminUser[]): Promise<AdminUser[]> => {
    try {
      const enrichedUsers = await Promise.all(
        userList.map(async (user) => {
          // If user already has deviceInfo with IP, return as is
          if (user.deviceInfo?.ipAddress) {
            return user;
          }

          // Try to get IP from user's earliest session (signup session)
          try {
            const sessions = await sessionService.getUserSessions(user.id);
            if (sessions.length > 0) {
              // Sort by createdAt to get the earliest session
              const sortedSessions = [...sessions].sort((a, b) => {
                const aTime = a.createdAt?.toDate?.()?.getTime() || 0;
                const bTime = b.createdAt?.toDate?.()?.getTime() || 0;
                return aTime - bTime; // Earliest first
              });

              const earliestSession = sortedSessions[0];
              if (earliestSession?.location?.ip) {
                // Enrich user with IP and location from session
                return {
                  ...user,
                  deviceInfo: {
                    ipAddress: earliestSession.location.ip,
                    userAgent: user.deviceInfo?.userAgent || earliestSession.deviceInfo?.userAgent || '',
                    operatingSystem: user.deviceInfo?.operatingSystem || earliestSession.deviceInfo?.os || '',
                    browser: user.deviceInfo?.browser || earliestSession.deviceInfo?.browser || '',
                    deviceType: user.deviceInfo?.deviceType || (earliestSession.deviceInfo?.device === 'mobile' ? 'mobile' : earliestSession.deviceInfo?.device === 'tablet' ? 'tablet' : 'desktop') as 'desktop' | 'mobile' | 'tablet',
                    lastSeen: user.deviceInfo?.lastSeen,
                    city: earliestSession.location.city || user.deviceInfo?.city,
                    country: earliestSession.location.country || user.deviceInfo?.country
                  }
                };
              }
            }
          } catch (sessionError) {
            console.warn(`Failed to get sessions for user ${user.id}:`, sessionError);
          }

          return user;
        })
      );

      return enrichedUsers;
    } catch (error) {
      console.error('Error enriching users with session IPs:', error);
      return userList;
    }
  }, []);

  // Load churn risk users
  const loadChurnRiskUsers = useCallback(async (userList: AdminUser[]) => {
    try {
      const now = new Date();
      const churnUsers = userList
        .map(user => {
          const lastLogin = user.lastLoginAt?.toDate() || user.lastActive?.toDate();
          if (!lastLogin) {
            // User never logged in - high risk
            const daysSinceSignup = Math.floor((now.getTime() - user.createdAt.toDate().getTime()) / (1000 * 60 * 60 * 24));
            return {
              user,
              daysSinceLastLogin: daysSinceSignup,
              riskLevel: daysSinceSignup > 30 ? 'high' : daysSinceSignup > 14 ? 'medium' : 'low' as 'low' | 'medium' | 'high'
            };
          }

          const daysSinceLastLogin = Math.floor((now.getTime() - lastLogin.getTime()) / (1000 * 60 * 60 * 24));
          let riskLevel: 'low' | 'medium' | 'high' = 'low';

          if (daysSinceLastLogin > 30) {
            riskLevel = 'high';
          } else if (daysSinceLastLogin > 14) {
            riskLevel = 'medium';
          }

          return {
            user,
            daysSinceLastLogin,
            riskLevel
          };
        })
        .filter(item => item.daysSinceLastLogin >= 7) // Only show users inactive for 7+ days
        .sort((a, b) => b.daysSinceLastLogin - a.daysSinceLastLogin)
        .slice(0, 10); // Top 10 churn risk users

      setChurnRiskUsers(churnUsers);
    } catch (error) {
      console.error('Error loading churn risk users:', error);
    }
  }, []);

  // Load high-value users
  const loadHighValueUsers = useCallback(async (userList: AdminUser[]) => {
    try {
      // Previously this downloaded ALL campaigns + ALL contacts to filter by userId.
      // Now we fetch per-user counts in parallel using getCountFromServer —
      // cost: 2 reads per user checked (capped at top 20 by activity), vs
      // reading every campaign and every contact in the entire database.
      const TOP_N = 20;
      const candidates = userList.slice(0, TOP_N);

      const userMetrics = await Promise.all(candidates.map(async (user) => {
        const [campaignSnap, contactSnap] = await Promise.all([
          getCountFromServer(query(collection(db, 'campaigns'), where('userId', '==', user.id))),
          getCountFromServer(query(collection(db, 'contacts'),  where('userId', '==', user.id))),
        ]);
        const campaignCount = campaignSnap.data().count;
        const contactCount  = contactSnap.data().count;

        let totalRevenue = 0;
        if (user.subscription?.plan === 'pro') {
          const monthsActive = user.subscription.startDate
            ? Math.floor((Date.now() - (user.subscription.startDate as any).toDate().getTime()) / (1000 * 60 * 60 * 24 * 30))
            : 0;
          totalRevenue = monthsActive * 9;
        }

        const campaignsScore  = Math.min(campaignCount * 5, 40);
        const contactsScore   = Math.min(contactCount / 10, 30);
        const loginScore = user.lastLoginAt
          ? Math.min(Math.floor((Date.now() - (user.lastLoginAt as any).toDate().getTime()) / (1000 * 60 * 60 * 24)), 30)
          : 0;
        const engagementScore = Math.min(campaignsScore + contactsScore + loginScore, 100);

        return {
          user,
          totalRevenue,
          totalCampaigns: campaignCount,
          totalContacts: contactCount,
          engagementScore,
        };
      }));

      // Sort by engagement score and revenue, take top 10
      const highValue = userMetrics
        .sort((a, b) => {
          if (b.engagementScore !== a.engagementScore) return b.engagementScore - a.engagementScore;
          return b.totalRevenue - a.totalRevenue;
        })
        .slice(0, 10);

      setHighValueUsers(highValue);
    } catch (error) {
      console.error('Error loading high-value users:', error);
    }
  }, []);

  // Load geographic distribution — one country per user (device info, then latest session)
  const loadGeographicDistribution = useCallback(async (userList: AdminUser[]) => {
    try {
      setIsGeoLoading(true);
      const userToCountry = new Map<string, string>();

      userList.forEach((user) => {
        const country = user.deviceInfo?.country?.trim();
        if (country) userToCountry.set(user.id, country);
      });

      try {
        // limit(200) — userSessions grows without bound (historical sessions
        // never deleted); we only need recent sessions for geo distribution.
        const sessionsSnapshot = await getDocs(
          query(collection(db, 'userSessions'), orderBy('lastActivity', 'desc'), limit(200))
        );
        const latestByUser = new Map<string, { country: string; time: number }>();

        sessionsSnapshot.docs.forEach((docSnap) => {
          const session = docSnap.data() as Session;
          const country = session.location?.country?.trim();
          if (!country || !session.userId) return;

          const time =
            session.lastActivity?.toDate?.()?.getTime() ||
            session.createdAt?.toDate?.()?.getTime() ||
            0;
          const prev = latestByUser.get(session.userId);
          if (!prev || time > prev.time) {
            latestByUser.set(session.userId, { country, time });
          }
        });

        latestByUser.forEach(({ country }, userId) => {
          if (!userToCountry.has(userId)) {
            userToCountry.set(userId, country);
          }
        });
      } catch (sessionErr) {
        console.warn('Could not load sessions for geographic distribution:', sessionErr);
      }

      const locationMap = new Map<string, number>();
      userToCountry.forEach((country) => {
        const topoId = resolveCountryToTopoId(country);
        const key = topoId || country;
        locationMap.set(key, (locationMap.get(key) || 0) + 1);
      });

      setGeographicDistribution(locationMap);
    } catch (error) {
      console.error('Error loading geographic distribution:', error);
    } finally {
      setIsGeoLoading(false);
    }
  }, []);

  // Load recent login activity
  const loadRecentLogins = useCallback(async (userList: AdminUser[]) => {
    try {
      const sessionsSnapshot = await getDocs(
        query(
          collection(db, 'userSessions'),
          orderBy('createdAt', 'desc'),
          limit(20)
        )
      );

      const recent = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Session));

      // Enrich with user data
      const loginsWithUsers = recent.map(session => {
        const user = userList.find(u => u.id === session.userId);
        return { ...session, user };
      });

      setRecentLogins(loginsWithUsers);
    } catch (error) {
      console.error('Error loading recent logins:', error);
    }
  }, []);

  // Detect suspicious activity
  const detectSuspiciousActivity = useCallback(async (userList: AdminUser[]) => {
    try {
      const suspicious: Array<{
        userId: string;
        user?: AdminUser;
        type: string;
        description: string;
        severity: 'low' | 'medium' | 'high';
        timestamp: Date;
      }> = [];

      // limit(200) — only need recent sessions for suspicious activity detection.
      const sessionsSnapshot = await getDocs(
        query(collection(db, 'userSessions'), orderBy('lastActivity', 'desc'), limit(200))
      );
      const allSessions = sessionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as Session));

      // Group sessions by user
      const userSessionsMap = new Map<string, Session[]>();
      allSessions.forEach(session => {
        if (!userSessionsMap.has(session.userId)) {
          userSessionsMap.set(session.userId, []);
        }
        userSessionsMap.get(session.userId)!.push(session);
      });

      // Check for suspicious patterns
      userSessionsMap.forEach((sessions, userId) => {
        const user = userList.find(u => u.id === userId);

        // Check for multiple locations
        const locations = new Set(
          sessions
            .filter(s => s.location?.country)
            .map(s => `${s.location.city || ''}, ${s.location.country}`)
            .filter(Boolean)
        );

        if (locations.size > 3) {
          suspicious.push({
            userId,
            user,
            type: 'multiple_locations',
            description: `User logged in from ${locations.size} different locations`,
            severity: 'high',
            timestamp: new Date()
          });
        }

        // Check for multiple active sessions
        const activeSessions = sessions.filter(s => s.isActive);
        if (activeSessions.length > 5) {
          suspicious.push({
            userId,
            user,
            type: 'multiple_sessions',
            description: `User has ${activeSessions.length} active sessions simultaneously`,
            severity: 'medium',
            timestamp: new Date()
          });
        }
      });

      // Sort by severity and timestamp
      suspicious.sort((a, b) => {
        const severityOrder = { high: 3, medium: 2, low: 1 };
        const severityDiff = severityOrder[b.severity] - severityOrder[a.severity];
        if (severityDiff !== 0) return severityDiff;
        return b.timestamp.getTime() - a.timestamp.getTime();
      });

      setSuspiciousActivities(suspicious.slice(0, 10)); // Top 10
    } catch (error) {
      console.error('Error detecting suspicious activity:', error);
    }
  }, []);


  const loadDashboardData = useCallback(async () => {
    try {
      setIsMetricsLoading(true);
      // Fetch ONLY core metrics first
      const metrics = await AdminService.getSystemMetrics();

      setStats({
        ...metrics,
        recentSignups: 0,
        revenueToday: 0,
        emailsSentThisWeek: 0,
        systemHealth: metrics.systemUptime > 99 ? 'healthy' : metrics.systemUptime > 95 ? 'warning' : 'critical',
      });
      setIsMetricsLoading(false);

      setIsUsersLoading(true);
      try {
        const userList = await AdminService.getAllUsers({ dedupeByEmail: false });
        setUsers(userList);
        setIsUsersLoading(false);

        loadOverviewAccurateMetrics(AdminService.dedupeUsersByEmail(userList))
          .then((accurate) => {
            setStats((prev) => ({
              ...prev,
              totalUsers: accurate.totalUsers,
              activeUsers: accurate.activeUsers,
              paidUsers: accurate.paidUsers,
              unpaidUsers: Math.max(0, accurate.totalUsers - accurate.paidUsers),
              freeUsers: Math.max(0, accurate.totalUsers - accurate.paidUsers),
              premiumUsers: accurate.paidUsers,
              newUsersThisMonth: accurate.newUsersThisMonth,
              newUsersLastMonth: accurate.newUsersLastMonth,
              userGrowthRate: accurate.userGrowthRate,
              revenueToday: accurate.revenueToday,
              revenueThisMonth: accurate.revenueThisMonth,
              revenueLastMonth: accurate.revenueLastMonth,
              totalRevenue: accurate.totalRevenue,
              emailsSentToday: accurate.emailsSentToday,
              emailsSentThisMonth: accurate.emailsSentThisMonth,
              emailsSentThisWeek: accurate.emailsSentThisWeek,
              totalEmailsSent: accurate.totalEmailsSent,
              conversionRate:
                accurate.totalUsers > 0
                  ? Math.round((accurate.paidUsers / accurate.totalUsers) * 1000) / 10
                  : 0,
            }));
            setDailyEmailData(accurate.dailyEmailsLast7Days);
            setDailyRevenueData(accurate.dailyRevenueLast7Days);
          })
          .catch((err) => console.error('Failed to load accurate overview metrics:', err));

        enrichUsersWithSessionIPs(userList)
          .then((enrichedUsers) => setUsers(enrichedUsers))
          .catch((err) => console.warn('Enrichment failed:', err));

        await Promise.all([
          loadRecentLogins(userList).then(() => setIsLoginsLoading(false)),
          detectSuspiciousActivity(userList).then(() => setIsSuspiciousLoading(false)),
          loadChurnRiskUsers(userList).then(() => setIsChurnLoading(false)),
          loadHighValueUsers(userList).then(() => setIsHighValueLoading(false)),
          loadGeographicDistribution(userList),
          AdminService.getAllCampaigns()
            .then(setCampaigns)
            .catch((err) => console.error('Failed to load campaigns for global search:', err)),
          apiKeyService.getAllApiKeys().then(setApiKeys).catch((err) => console.error('Failed to load API keys for global search:', err)),
        ]);
      } catch (userErr: any) {
        console.error('Failed to load users:', userErr);
        setDataError('Failed to load users: ' + userErr.message);
        setIsUsersLoading(false);
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error);
      showError('Failed to load dashboard data');
    } finally {
      setIsLoading(false);
      setIsMetricsLoading(false);
      setIsUsersLoading(false);
    }
  }, [loadActiveSessions, loadRecentLogins, detectSuspiciousActivity, loadChurnRiskUsers, loadHighValueUsers, loadGeographicDistribution, enrichUsersWithSessionIPs, showError]);

  useEffect(() => {
    loadDashboardData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedUserIds(new Set());
  }, [userTab, userSearchTerm]);

  const reloadUsersList = useCallback(async () => {
    const userList = await AdminService.getAllUsers({ dedupeByEmail: false });
    setUsers(userList);
    enrichUsersWithSessionIPs(userList)
      .then((enrichedUsers) => setUsers(enrichedUsers))
      .catch(() => undefined);
  }, [enrichUsersWithSessionIPs]);

  const handleBulkDeleteUsers = async (userIds: string[]) => {
    if (userIds.length === 0) return;

    const preview = userIds
      .slice(0, 5)
      .map((id) => {
        const u = users.find((user) => user.id === id);
        return u?.email || id;
      })
      .join('\n• ');
    const more = userIds.length > 5 ? `\n• …and ${userIds.length - 5} more` : '';

    const confirmMessage =
      `PERMANENTLY DELETE ${userIds.length} user account(s)?\n\n` +
      `Each account will lose:\n` +
      `• Firebase Auth account\n` +
      `• Profile, workspaces, contacts, campaigns, automations\n` +
      `• Domains, billing records, storage files\n\n` +
      `Accounts:\n• ${preview}${more}\n\nThis CANNOT be undone.`;

    if (!window.confirm(confirmMessage)) return;

    const typed = window.prompt(
      `Type DELETE to permanently remove ${userIds.length} user account(s) and ALL their data:`
    );
    if (typed?.trim() !== 'DELETE') {
      showError('Bulk delete cancelled — type DELETE exactly to confirm.');
      return;
    }

    setBulkDeleting(true);
    setBulkDeleteProgress({ current: 0, total: userIds.length });
    try {
      const result = await AdminService.deleteUsers(userIds, (completed, total) => {
        setBulkDeleteProgress({ current: completed + 1, total });
      });
      setSelectedUserIds(new Set());

      if (result.failedCount === 0) {
        showSuccess(`Permanently deleted ${result.successCount} user account(s).`);
      } else {
        const failedList = result.results
          .filter((r) => !r.success)
          .map((r) => `${r.email || r.userId}: ${r.error || 'failed'}`)
          .join('; ');
        showError(
          `Deleted ${result.successCount}, failed ${result.failedCount}. ${failedList}`
        );
      }

      await reloadUsersList();
      loadDashboardData().catch(() => undefined);
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
      setBulkDeleteProgress(null);
    }
  };



  // Real-time presence: subscribe once users are loaded, unsubscribe on unmount/re-load.
  useEffect(() => {
    if (users.length === 0) return;
    const unsubscribe = loadActiveSessions(users);
    return () => unsubscribe();
  }, [users, loadActiveSessions]);

  // Real-time clock update
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Load admin profile picture from localStorage
  useEffect(() => {
    const loadProfilePicture = () => {
      const savedProfilePicture = localStorage.getItem('adminProfilePicture');
      if (savedProfilePicture) {
        setAdminProfilePicture(savedProfilePicture);
      }
    };

    // Load initially
    loadProfilePicture();

    // Listen for profile picture updates
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'adminProfilePicture') {
        loadProfilePicture();
      }
    };

    // Listen for custom profile picture update events
    const handleProfileUpdate = () => {
      loadProfilePicture();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('profilePictureUpdated', handleProfileUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('profilePictureUpdated', handleProfileUpdate);
    };
  }, []);

  const handleUserClick = (user: AdminUser) => {
    navigate(`/admin/control-panel-7x9k2m/user-details/${user.id}`);
  };

  const handleBlockUser = async (user: AdminUser) => {
    try {
      await AdminService.blockUser(user.id);
      // Update the user in the local state
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === user.id ? { ...u, status: 'blocked' } : u
        )
      );
      setBlockConfirmation({ user: null, isOpen: false });
      showError('User has been blocked and signed out');
    } catch (error) {
      console.error('Error blocking user:', error);
      showError('Failed to block user');
    }
  };

  const handleUnblockUser = async (user: AdminUser) => {
    try {
      await AdminService.unblockUser(user.id);
      // Update the user in the local state
      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === user.id ? { ...u, status: 'active' } : u
        )
      );
      showError('User has been unblocked');
    } catch (error) {
      console.error('Error unblocking user:', error);
      showError('Failed to unblock user');
    }
  };

  const handleCleanupDuplicates = async () => {
    try {
      const result = await AdminService.cleanupDuplicateUsers();
      if (result.cleaned > 0) {
        showError(`Cleaned up ${result.cleaned} duplicate user records`);
        // Reload the user list to reflect changes
        await loadDashboardData();
      } else {
        showError('No duplicate users found');
      }
    } catch (error) {
      console.error('Error cleaning up duplicates:', error);
      showError('Failed to clean up duplicate users');
    }
  };

  const renderOverviewTab = () => (
    <div className="space-y-4">
      {/* Real-time World Map Integration */}
      <AdminWorldMap
        isDark={isDark}
        activeSessions={activeSessions}
        geographicDistribution={geographicDistribution}
        isLoading={isGeoLoading}
        totalUsers={stats.totalUsers}
      />

      {/* User Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Users', value: stats.totalUsers, growth: 'Registered accounts (Firestore)', icon: 'M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z' },
          { label: 'Paid Subscribers', value: stats.paidUsers, growth: `${stats.totalUsers > 0 ? Math.round((stats.paidUsers / stats.totalUsers) * 100) : 0}% · active paid plan`, icon: 'M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h1.125A2.625 2.625 0 0121 13.125v-1.5c0-1.036-.84-1.875-1.875-1.875H18.75z' },
          { label: 'New Users (Month)', value: stats.newUsersThisMonth, growth: `${stats.userGrowthRate >= 0 ? '+' : ''}${stats.userGrowthRate}% vs ${stats.newUsersLastMonth} last month`, icon: 'M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.58A13.978 13.978 0 016.982 4.718a12.014 12.014 0 015.036 0 13.978 13.978 0 01.94 3.198m0 0a3 3 0 104.682 2.72M15 18.72c.195.385.293.802.293 1.22 0 .418-.098.835-.293 1.22m-4.586-2.44a3.001 3.001 0 010-4.242m4.586 4.242a3 3 0 11-4.586-2.44' },
          { label: 'Active (30 days)', value: stats.activeUsers, growth: 'Logged in within the last 30 days', icon: 'M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z' }
        ].map((item, i) => (
          <div key={i} className={`rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700 shadow-lg' : 'bg-gray-100 shadow-md'} p-4 transition-colors ${isMetricsLoading ? 'animate-pulse' : ''}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{item.label}</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {isMetricsLoading ? '...' : item.value}
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{item.growth}</p>
              </div>
              <div className="flex items-center justify-center">
                <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Revenue Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className={`rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700 shadow-lg' : 'bg-gray-100 shadow-md'} p-4 transition-colors`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Revenue Today</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ${stats.revenueToday.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Paid invoices (UTC day)</p>
            </div>
            <div className="flex items-center justify-center">
              <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
              </svg>
            </div>
          </div>
        </div>

        <div className={`rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700 shadow-lg' : 'bg-gray-100 shadow-md'} p-4 transition-colors`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Revenue This Month</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                ${stats.revenueThisMonth.toLocaleString()}
              </p>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                vs ${stats.revenueLastMonth.toLocaleString()} last month · from invoices
              </p>
            </div>
            <div className="flex items-center justify-center">
              <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h1.125A2.625 2.625 0 0121 13.125v-1.5c0-1.036-.84-1.875-1.875-1.875H18.75z" />
              </svg>
            </div>
          </div>
        </div>

        <div className={`rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700 shadow-lg' : 'bg-gray-100 shadow-md'} p-4 transition-colors`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>Revenue Last Month</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'
                }`}>${stats.revenueLastMonth.toLocaleString()}</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>Previous calendar month · paid invoices</p>
            </div>
            <div className="flex items-center justify-center">
              <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
            </div>
          </div>
        </div>

        <div className={`rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700 shadow-lg' : 'bg-gray-100 shadow-md'} p-4 transition-colors`}>
          <div className="flex items-center justify-between">
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'
                }`}>Total Revenue</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'
                }`}>${stats.totalRevenue.toLocaleString()}</p>
              <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>All-time paid invoices</p>
            </div>
            <div className="flex items-center justify-center">
              <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Additional Boxes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className={`rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700 shadow-lg' : 'bg-gray-100 shadow-md'} p-6 min-h-[300px] transition-colors`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'
                }`}>Analytics Report</h3>
              <p className={`text-xs mt-1 font-light ${isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>Emails sent & paid revenue (last 7 days)</p>
            </div>
            <div className="flex items-center justify-center">
              <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
            </div>
          </div>

          {/* Analytics Line Graph */}
          {(() => {
            // Calculate graph data from real daily email data
            const data = dailyEmailData.length > 0 ? dailyEmailData : [0, 0, 0, 0, 0, 0, 0];
            const maxValue = Math.max(...data, 1); // Avoid division by zero
            const minValue = Math.min(...data);
            const range = maxValue - minValue || 1;

            // Calculate Y positions (inverted: higher values = lower Y)
            const graphHeight = 120; // Height of graph area
            const graphTop = 40;
            const graphBottom = graphTop + graphHeight;
            const points = data.map((value, index) => {
              const normalizedValue = range > 0 ? (value - minValue) / range : 0.5;
              const y = graphBottom - (normalizedValue * graphHeight);
              const x = 40 + (index * (340 / 6)); // 6 intervals for 7 points
              return { x, y, value };
            });

            // Smooth curve helper
            const getPath = (pts: { x: number, y: number }[]) => {
              return pts.map((point, index) => {
                if (index === 0) return `M ${point.x} ${point.y}`;
                const prevPoint = pts[index - 1];
                const cp1x = prevPoint.x + (point.x - prevPoint.x) / 2;
                return `C ${cp1x} ${prevPoint.y}, ${cp1x} ${point.y}, ${point.x} ${point.y}`;
              }).join(' ');
            };

            const pathData = getPath(points);

            const revData = dailyRevenueData.length > 0 ? dailyRevenueData : [0, 0, 0, 0, 0, 0, 0];
            const maxRev = Math.max(...revData, 1);
            const minRev = Math.min(...revData);
            const revRange = maxRev - minRev || 1;
            const revPoints = revData.map((value, index) => {
              const normalizedValue = revRange > 0 ? (value - minRev) / revRange : 0.5;
              const y = graphBottom - normalizedValue * graphHeight;
              const x = 40 + index * (340 / 6);
              return { x, y, value };
            });
            const revPathData = getPath(revPoints);

            // Area path
            const areaPath = `${pathData} L ${points[points.length - 1].x} ${graphBottom} L ${points[0].x} ${graphBottom} Z`;

            // Get day labels for last 7 days
            const dayLabels = [];
            for (let i = 6; i >= 0; i--) {
              const date = new Date();
              date.setDate(date.getDate() - i);
              dayLabels.push(date.toLocaleDateString('en-US', { weekday: 'short' }));
            }

            // Calculate stats
            const total = data.reduce((sum, val) => sum + val, 0);
            const avg = data.length > 0 ? Math.round(total / data.length) : 0;
            const firstHalf = data.slice(0, Math.ceil(data.length / 2)).reduce((sum, val) => sum + val, 0);
            const secondHalf = data.slice(Math.ceil(data.length / 2)).reduce((sum, val) => sum + val, 0);
            const growth = firstHalf > 0 ? ((secondHalf - firstHalf) / firstHalf) * 100 : 0;

            return (
              <>
                <div className="w-full h-48 relative">
                  <svg viewBox="0 0 400 200" className="w-full h-full" preserveAspectRatio="none">
                    {/* Grid lines */}
                    <defs>
                      <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={isDark ? "rgba(20, 184, 166, 0.2)" : "rgba(20, 184, 166, 0.1)"} />
                        <stop offset="100%" stopColor={isDark ? "rgba(20, 184, 166, 0)" : "rgba(20, 184, 166, 0)"} />
                      </linearGradient>
                      <linearGradient id="revGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor={isDark ? "rgba(59, 130, 246, 0.1)" : "rgba(59, 130, 246, 0.05)"} />
                        <stop offset="100%" stopColor={isDark ? "rgba(59, 130, 246, 0)" : "rgba(59, 130, 246, 0)"} />
                      </linearGradient>
                    </defs>

                    {/* Horizontal grid lines */}
                    {[0, 1, 2, 3].map((i) => (
                      <line
                        key={i}
                        x1="40"
                        y1={graphTop + i * 40}
                        x2="380"
                        y2={graphTop + i * 40}
                        stroke={isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)"}
                        strokeDasharray="4 4"
                      />
                    ))}

                    {/* Revenue Trend Area & Line (Secondary) */}
                    <path
                      d={`${revPathData} L 380 ${graphBottom} L 40 ${graphBottom} Z`}
                      fill="url(#revGradient)"
                    />
                    <path
                      d={revPathData}
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="1"
                      strokeOpacity="0.3"
                      strokeDasharray="4 2"
                    />

                    {/* Main Area under the curve */}
                    <path
                      d={areaPath}
                      fill="url(#lineGradient)"
                    />

                    {/* Main line */}
                    <path
                      d={pathData}
                      fill="none"
                      stroke={isDark ? "#14b8a6" : "#0d9488"}
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />

                    {/* Data points */}
                    {points.map((point, index) => (
                      <circle
                        key={index}
                        cx={point.x}
                        cy={point.y}
                        r="3"
                        fill={isDark ? "#14b8a6" : "#0d9488"}
                      />
                    ))}

                    {/* X-axis labels */}
                    {dayLabels.map((day, index) => (
                      <text
                        key={index}
                        x={40 + index * (340 / 6)}
                        y="190"
                        textAnchor="middle"
                        fontSize="10"
                        fill={isDark ? "rgba(255, 255, 255, 0.4)" : "rgba(0, 0, 0, 0.4)"}
                        fontFamily="system-ui"
                        fontWeight="300"
                      >
                        {day}
                      </text>
                    ))}
                  </svg>
                  {/* Legend */}
                  <div className="absolute top-2 right-2 flex items-center space-x-3">
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 rounded-full bg-teal-500"></div>
                      <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Emails</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <div className="w-2 h-2 rounded-full bg-blue-500 opacity-40"></div>
                      <span className={`text-[10px] ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Revenue</span>
                    </div>
                  </div>
                </div>

                {/* Stats below graph */}
                <div className={`grid grid-cols-3 gap-2 mt-4 pt-4 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div>
                    <p className={`text-[10px] uppercase font-bold tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Total Sends</p>
                    <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{total.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className={`text-[10px] uppercase font-bold tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Growth</p>
                    <p className={`text-lg font-bold ${growth >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {growth >= 0 ? '+' : ''}{growth.toFixed(1)}%
                    </p>
                  </div>
                  <div>
                    <p className={`text-[10px] uppercase font-bold tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Daily Avg</p>
                    <p className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{avg.toLocaleString()}</p>
                  </div>
                </div>
              </>
            );
          })()}
        </div>

        <div className={`rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700 shadow-lg' : 'bg-gray-100 shadow-md'} p-6 min-h-[300px] transition-colors`}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'
                }`}>Usage Statistics</h3>
              <p className={`text-xs mt-1 font-light ${isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>Resource consumption overview</p>
            </div>
            <div className="flex items-center justify-center">
              <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
              </svg>
            </div>
          </div>

          {/* Usage Items */}
          <div className="space-y-6">
            {/* Email Usage - Today */}
            {(() => {
              const emailsToday = stats.emailsSentToday || 0;
              const formatNumber = (num: number) => num.toLocaleString();

              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <svg className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                      </svg>
                      <span className={`text-sm font-light ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Emails Sent Today</span>
                    </div>
                    <span className={`text-sm font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatNumber(emailsToday)}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Email Usage - This Week */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <svg className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                  </svg>
                  <span className={`text-sm font-light ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Emails Sent (7 days)</span>
                </div>
                <span className={`text-sm font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {(stats.emailsSentThisWeek || 0).toLocaleString()}
                </span>
              </div>
            </div>

            {/* Email Usage - This Month */}
            {(() => {
              const emailsThisMonth = stats.emailsSentThisMonth || 0;
              const formatNumber = (num: number) => num.toLocaleString();

              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <svg className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                      </svg>
                      <span className={`text-sm font-light ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Emails Sent This Month</span>
                    </div>
                    <span className={`text-sm font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatNumber(emailsThisMonth)}
                    </span>
                  </div>
                </div>
              );
            })()}

            {/* Email Usage - Total */}
            {(() => {
              const emailsTotal = stats.totalEmailsSent || 0;
              const formatNumber = (num: number) => num.toLocaleString();

              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <svg className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                      </svg>
                      <span className={`text-sm font-light ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total Emails Sent</span>
                    </div>
                    <span className={`text-sm font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {formatNumber(emailsTotal)}
                    </span>
                  </div>
                </div>
              );
            })()}

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <svg className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
                  </svg>
                  <span className={`text-sm font-light ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total Contacts</span>
                </div>
                <span className={`text-sm font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {stats.totalContacts.toLocaleString()}
                </span>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <svg className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
                  </svg>
                  <span className={`text-sm font-light ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Total Campaigns</span>
                </div>
                <span className={`text-sm font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {stats.totalCampaigns.toLocaleString()}
                </span>
              </div>
            </div>

          </div>
        </div>

        {/* Box 3 - Recent Signup Users */}
        {(() => {
          const usersPerPage = 5;
          // Sort users by creation date (most recent first)
          const sortedUsers = [...users].sort((a, b) => {
            const dateA = a.createdAt?.seconds || 0;
            const dateB = b.createdAt?.seconds || 0;
            return dateB - dateA;
          });

          const totalPages = Math.ceil(sortedUsers.length / usersPerPage);
          const startIndex = (recentUsersPage - 1) * usersPerPage;
          const endIndex = startIndex + usersPerPage;
          const paginatedUsers = sortedUsers.slice(startIndex, endIndex);

          const formatDate = (timestamp: any) => {
            if (!timestamp || !timestamp.seconds) return 'N/A';
            const date = new Date(timestamp.seconds * 1000);
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
          };

          return (
            <div className={`col-span-full rounded-lg ${isDark ? 'bg-gray-800 border border-gray-700 shadow-lg' : 'bg-gray-100 shadow-md'} p-6 min-h-[300px] transition-colors`}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'
                    }`}>Recent Signups</h3>
                  <p className={`text-xs mt-1 font-light ${isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Latest user registrations</p>
                </div>
                <div className="flex items-center justify-center">
                  <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.58A13.978 13.978 0 016.982 4.718a12.014 12.014 0 015.036 0 13.978 13.978 0 01.94 3.198m0 0a3 3 0 104.682 2.72M15 18.72c.195.385.293.802.293 1.22 0 .418-.098.835-.293 1.22m-4.586-2.44a3.001 3.001 0 010-4.242m4.586 4.242a3 3 0 11-4.586-2.44" />
                  </svg>
                </div>
              </div>

              {/* Users Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className={`border-b ${isDark ? 'border-gray-600' : 'border-gray-300'}`}>
                      <th className={`text-left py-2 px-2 text-xs font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>User</th>
                      <th className={`text-left py-2 px-2 text-xs font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Email</th>
                      <th className={`text-left py-2 px-2 text-xs font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>IP Address</th>
                      <th className={`text-left py-2 px-2 text-xs font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUsers.length > 0 ? (
                      paginatedUsers.map((user) => (
                        <tr key={user.id} className={`border-b transition-colors hover:bg-opacity-50 ${isDark ? 'border-gray-600 hover:bg-gray-700/30' : 'border-gray-300 hover:bg-gray-50'
                          }`}>
                          <td className="py-3 px-2">
                            <div className="flex items-center space-x-2">
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-light ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                                }`}>
                                {user.email?.charAt(0).toUpperCase() || 'U'}
                              </div>
                              <a
                                href={`/admin/control-panel-7x9k2m/user-details/${user.id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`text-sm font-light hover:underline transition-colors duration-200 ${isDark
                                  ? 'text-gray-300 hover:text-cyan-300'
                                  : 'text-gray-700 hover:text-cyan-600'
                                  }`}
                              >
                                {user.firstName} {user.lastName}
                              </a>
                            </div>
                          </td>
                          <td className={`py-3 px-2 text-sm font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {user.email || 'N/A'}
                          </td>
                          <td className={`py-3 px-2 text-sm font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {user.deviceInfo?.ipAddress ? (
                              <span
                                className="cursor-help"
                                title={
                                  user.deviceInfo?.city && user.deviceInfo?.country
                                    ? `${user.deviceInfo.city}, ${user.deviceInfo.country}`
                                    : user.deviceInfo?.country
                                      ? user.deviceInfo.country
                                      : 'Location not available'
                                }
                              >
                                {user.deviceInfo.ipAddress}
                              </span>
                            ) : 'Not available'}
                          </td>
                          <td className={`py-3 px-2 text-xs font-light ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            {formatDate(user.createdAt)}
                          </td>
                        </tr>
                      ))
                    ) : isUsersLoading ? (
                      [1, 2, 3, 4, 5].map(i => (
                        <tr key={i}>
                          <td colSpan={4} className="py-4 px-2">
                            <div className={`h-8 rounded animate-pulse ${isDark ? 'bg-gray-700/50' : 'bg-gray-200'}`}></div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className={`py-8 text-center text-sm font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          No users found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className={`flex items-center justify-between mt-4 pt-4 border-t ${isDark ? 'border-gray-600' : 'border-gray-300'
                  }`}>
                  <button
                    onClick={() => setRecentUsersPage(prev => Math.max(1, prev - 1))}
                    disabled={recentUsersPage === 1}
                    className={`px-3 py-1.5 text-sm font-light rounded transition-colors ${recentUsersPage === 1
                      ? isDark
                        ? 'text-gray-600 cursor-not-allowed'
                        : 'text-gray-400 cursor-not-allowed'
                      : isDark
                        ? 'text-gray-300 hover:text-white hover:bg-gray-700 border border-gray-600 hover:border-gray-500'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                      }`}
                  >
                    Previous
                  </button>
                  <span className={`text-xs font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Page {recentUsersPage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setRecentUsersPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={recentUsersPage === totalPages}
                    className={`px-3 py-1.5 text-sm font-light rounded transition-colors ${recentUsersPage === totalPages
                      ? isDark
                        ? 'text-gray-600 cursor-not-allowed'
                        : 'text-gray-400 cursor-not-allowed'
                      : isDark
                        ? 'text-gray-300 hover:text-white hover:bg-gray-700 border border-gray-600 hover:border-gray-500'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                      }`}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* Active Sessions and Recent Login Activity - Side by Side */}
        <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Active Sessions / Online Users */}
          <div className={`rounded-lg shadow-md p-6 min-h-[300px] ${isDark
            ? 'bg-gray-700'
            : 'bg-gray-100'
            }`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'
                  }`}>Active Sessions</h3>
                <p className={`text-xs mt-1 font-light ${isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>Users currently online ({activeSessions.length})</p>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.348 14.651a3.75 3.75 0 010-5.303m5.304 0a3.75 3.75 0 010 5.303m-7.425 2.122a6.75 6.75 0 010-9.546m9.546 0a6.75 6.75 0 010 9.546M5.106 18.894a9.75 9.75 0 010-13.788m13.788 0a9.75 9.75 0 010 13.788" />
                </svg>
              </div>
            </div>

            <div className="space-y-3">
              {activeSessions.length > 0 ? (
                activeSessions.map((session) => {
                  const lastActivity = session.lastActivity?.toDate();
                  const timeAgo = lastActivity ? Math.floor((Date.now() - lastActivity.getTime()) / 60000) : 0;
                  return (
                    <div key={session.id} className={`p-3 rounded-lg transition-colors ${isDark ? 'bg-gray-700/50 border border-gray-600/50 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
                      }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`w-2 h-2 rounded-full ${timeAgo < 5 ? 'bg-green-500 animate-pulse' : 'bg-gray-400'
                            }`}></div>
                          <div>
                            <a
                              href={`/admin/control-panel-7x9k2m/user-details/${session.userId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`text-sm font-light hover:underline ${isDark ? 'text-white hover:text-cyan-300' : 'text-gray-900 hover:text-cyan-600'
                                }`}
                            >
                              {session.user?.firstName} {session.user?.lastName} ({session.user?.email})
                            </a>
                            <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                              {session.location?.city && session.location?.country
                                ? `${session.location.city}, ${session.location.country}`
                                : session.location?.country || 'Unknown location'}
                              {' • '}
                              {session.deviceInfo?.device} • {session.deviceInfo?.browser}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {timeAgo < 1 ? 'Just now' : `${timeAgo} min ago`}
                          </p>
                          <p
                            className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'} cursor-help`}
                            title={
                              session.location?.city && session.location?.country
                                ? `${session.location.city}, ${session.location.country}`
                                : session.location?.country
                                  ? session.location.country
                                  : 'Location not available'
                            }
                          >
                            {session.location?.ip || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : isSessionsLoading ? (
                <div className="flex flex-col space-y-4">
                  {[1, 2, 3].map(i => (
                    <div key={i} className={`h-16 rounded-lg animate-pulse ${isDark ? 'bg-gray-700/50' : 'bg-gray-200'}`}></div>
                  ))}
                </div>
              ) : (
                <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  No active sessions
                </p>
              )}
            </div>
          </div>

          {/* Recent Login Activity */}
          {(() => {
            const loginsPerPage = 5;
            const totalPages = Math.ceil(recentLogins.length / loginsPerPage);
            const startIndex = (recentLoginsPage - 1) * loginsPerPage;
            const endIndex = startIndex + loginsPerPage;
            const paginatedLogins = recentLogins.slice(startIndex, endIndex);

            const formatTime = (date: Date) => {
              const now = new Date();
              const diff = now.getTime() - date.getTime();
              const minutes = Math.floor(diff / 60000);
              if (minutes < 1) return 'Just now';
              if (minutes < 60) return `${minutes}m ago`;
              const hours = Math.floor(minutes / 60);
              if (hours < 24) return `${hours}h ago`;
              return date.toLocaleDateString();
            };

            return (
              <div className={`rounded-lg shadow-md p-6 min-h-[300px] ${isDark
                ? 'bg-gray-700'
                : 'bg-gray-100'
                }`}>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'
                      }`}>Recent Login Activity</h3>
                    <p className={`text-xs mt-1 font-light ${isDark ? 'text-gray-400' : 'text-gray-500'
                      }`}>Latest login events ({recentLogins.length} total)</p>
                  </div>
                  <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" />
                  </svg>
                </div>

                <div className="space-y-2">
                  {paginatedLogins.length > 0 ? (
                    paginatedLogins.map((session) => {
                      const loginTime = session.createdAt?.toDate();
                      return (
                        <div key={session.id} className={`p-3 rounded-lg transition-colors ${isDark ? 'bg-gray-700/50 border border-gray-600/50 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
                          }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${isDark ? 'bg-gray-700 text-gray-200' : 'bg-gray-200 text-gray-700'
                                }`}>
                                {session.user?.email?.charAt(0).toUpperCase() || 'U'}
                              </div>
                              <div>
                                <a
                                  href={`/admin/control-panel-7x9k2m/user-details/${session.userId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className={`text-sm font-light hover:underline ${isDark ? 'text-white hover:text-cyan-300' : 'text-gray-900 hover:text-cyan-600'
                                    }`}
                                >
                                  {session.user?.firstName} {session.user?.lastName}
                                </a>
                                <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                  {session.location?.city && session.location?.country
                                    ? `${session.location.city}, ${session.location.country}`
                                    : session.location?.country || 'Unknown'}
                                  {' • '}
                                  {session.deviceInfo?.device} • {session.deviceInfo?.browser}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {loginTime ? formatTime(loginTime) : 'N/A'}
                              </p>
                              <p
                                className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'} cursor-help`}
                                title={
                                  session.location?.city && session.location?.country
                                    ? `${session.location.city}, ${session.location.country}`
                                    : session.location?.country
                                      ? session.location.country
                                      : 'Location not available'
                                }
                              >
                                {session.location?.ip || 'N/A'}
                              </p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      No recent logins
                    </p>
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className={`flex items-center justify-between mt-4 pt-4 border-t ${isDark ? 'border-gray-600' : 'border-gray-300'
                    }`}>
                    <button
                      onClick={() => setRecentLoginsPage(prev => Math.max(1, prev - 1))}
                      disabled={recentLoginsPage === 1}
                      className={`px-3 py-1.5 text-sm font-light rounded transition-colors ${recentLoginsPage === 1
                        ? isDark
                          ? 'text-gray-600 cursor-not-allowed'
                          : 'text-gray-400 cursor-not-allowed'
                        : isDark
                          ? 'text-gray-300 hover:text-white hover:bg-gray-700 border border-gray-600 hover:border-gray-500'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                        }`}
                    >
                      Previous
                    </button>
                    <span className={`text-xs font-light ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      Page {recentLoginsPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setRecentLoginsPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={recentLoginsPage === totalPages}
                      className={`px-3 py-1.5 text-sm font-light rounded transition-colors ${recentLoginsPage === totalPages
                        ? isDark
                          ? 'text-gray-600 cursor-not-allowed'
                          : 'text-gray-400 cursor-not-allowed'
                        : isDark
                          ? 'text-gray-300 hover:text-white hover:bg-gray-700 border border-gray-600 hover:border-gray-500'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200'
                        }`}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            );
          })()}
        </div>

        {/* Suspicious Activity Alerts */}
        {suspiciousActivities.length > 0 && (
          <div className={`col-span-full rounded-lg shadow-md p-6 min-h-[300px] ${isDark
            ? 'bg-gray-700'
            : 'bg-gray-100'
            }`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'
                  }`}>Suspicious Activity Alerts</h3>
                <p className={`text-xs mt-1 font-light ${isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>Unusual patterns detected ({suspiciousActivities.length})</p>
              </div>
              <svg className={`w-6 h-6 ${isDark ? 'text-red-400' : 'text-red-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>

            <div className="space-y-3">
              {suspiciousActivities.map((activity, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 transition-colors ${activity.severity === 'high'
                  ? isDark ? 'bg-red-900/30 border-red-500 hover:bg-red-900/40' : 'bg-red-50 border-red-500 hover:bg-red-100'
                  : activity.severity === 'medium'
                    ? isDark ? 'bg-yellow-900/30 border-yellow-500 hover:bg-yellow-900/40' : 'bg-yellow-50 border-yellow-500 hover:bg-yellow-100'
                    : isDark ? 'bg-orange-900/30 border-orange-500 hover:bg-orange-900/40' : 'bg-orange-50 border-orange-500 hover:bg-orange-100'
                  }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${activity.severity === 'high'
                          ? 'bg-red-500 text-white'
                          : activity.severity === 'medium'
                            ? 'bg-yellow-500 text-white'
                            : 'bg-orange-500 text-white'
                          }`}>
                          {activity.severity.toUpperCase()}
                        </span>
                        <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {activity.type.replace('_', ' ')}
                        </span>
                      </div>
                      <a
                        href={`/admin/control-panel-7x9k2m/user-details/${activity.userId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`text-sm font-light hover:underline ${isDark ? 'text-white hover:text-cyan-300' : 'text-gray-900 hover:text-cyan-600'
                          }`}
                      >
                        {activity.user?.firstName} {activity.user?.lastName} ({activity.user?.email})
                      </a>
                      <p className={`text-sm mt-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                        {activity.description}
                      </p>
                    </div>
                    <p className={`text-xs ml-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {activity.timestamp.toLocaleTimeString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Churn Risk Users and High-Value Users - Side by Side */}
        <div className="col-span-full grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Churn Risk Users */}
          {churnRiskUsers.length > 0 && (
            <div className={`rounded-lg shadow-md p-6 min-h-[300px] ${isDark
              ? 'bg-gray-700'
              : 'bg-gray-100'
              }`}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'
                    }`}>Churn Risk Users</h3>
                  <p className={`text-xs mt-1 font-light ${isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Users inactive for 7+ days ({churnRiskUsers.length})</p>
                </div>
                <svg className={`w-6 h-6 ${isDark ? 'text-orange-400' : 'text-orange-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
              </div>

              <div className="space-y-3">
                {churnRiskUsers.map((item) => (
                  <div key={item.user.id} className={`p-3 rounded-lg transition-colors ${isDark ? 'bg-gray-700/50 border border-gray-600/50 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-2 h-2 rounded-full ${item.riskLevel === 'high' ? 'bg-red-500' : item.riskLevel === 'medium' ? 'bg-yellow-500' : 'bg-orange-500'
                          }`}></div>
                        <div>
                          <a
                            href={`/admin/control-panel-7x9k2m/user-details/${item.user.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-sm font-light hover:underline ${isDark ? 'text-white hover:text-cyan-300' : 'text-gray-900 hover:text-cyan-600'
                              }`}
                          >
                            {item.user.firstName} {item.user.lastName}
                          </a>
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {item.user.email}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`text-xs px-2 py-1 rounded ${item.riskLevel === 'high'
                          ? isDark ? 'bg-red-900/30 text-red-300' : 'bg-red-100 text-red-800'
                          : item.riskLevel === 'medium'
                            ? isDark ? 'bg-yellow-900/30 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                            : isDark ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-800'
                          }`}>
                          {item.riskLevel.toUpperCase()}
                        </span>
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {item.daysSinceLastLogin} days ago
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* High-Value Users */}
          {highValueUsers.length > 0 && (
            <div className={`rounded-lg shadow-md p-6 min-h-[300px] ${isDark
              ? 'bg-gray-700'
              : 'bg-gray-100'
              }`}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'
                    }`}>High-Value Users</h3>
                  <p className={`text-xs mt-1 font-light ${isDark ? 'text-gray-400' : 'text-gray-500'
                    }`}>Top users by engagement ({highValueUsers.length})</p>
                </div>
                <svg className={`w-6 h-6 ${isDark ? 'text-green-400' : 'text-green-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
              </div>

              <div className="space-y-3">
                {highValueUsers.map((item) => (
                  <div key={item.user.id} className={`p-3 rounded-lg transition-colors ${isDark ? 'bg-gray-700/50 border border-gray-600/50 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
                    }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${isDark ? 'bg-green-600 text-white' : 'bg-green-200 text-green-700'
                          }`}>
                          {item.user.email?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div>
                          <a
                            href={`/admin/control-panel-7x9k2m/user-details/${item.user.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`text-sm font-light hover:underline ${isDark ? 'text-white hover:text-cyan-300' : 'text-gray-900 hover:text-cyan-600'
                              }`}
                          >
                            {item.user.firstName} {item.user.lastName}
                          </a>
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {item.totalCampaigns} campaigns • {item.totalContacts.toLocaleString()} contacts
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-xs font-medium ${isDark ? 'text-green-400' : 'text-green-600'}`}>
                          Score: {item.engagementScore}
                        </p>
                        {item.totalRevenue > 0 && (
                          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            ${item.totalRevenue}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Geographic Distribution */}
        {geographicDistribution.size > 0 && (
          <div className={`col-span-full rounded-lg shadow-md p-6 min-h-[300px] ${isDark
            ? 'bg-gray-700'
            : 'bg-gray-100'
            }`}>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`text-lg font-light ${isDark ? 'text-white' : 'text-gray-900'
                  }`}>Geographic Distribution</h3>
                <p className={`text-xs mt-1 font-light ${isDark ? 'text-gray-400' : 'text-gray-500'
                  }`}>User locations by country ({geographicDistribution.size} countries)</p>
              </div>
              <svg className={`w-6 h-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3v1.5M3 21v-6m0 0l2.77-2.77a6 6 0 018.46 0L21 14.5M3 15h18m0 0v3.75m0-3.75l-2.77 2.77a6 6 0 01-8.46 0L3 11.25" />
              </svg>
            </div>

            <div className="space-y-2">
              {Array.from(geographicDistribution.entries())
                .sort((a, b) => b[1] - a[1])
                .slice(0, 10)
                .map(([country, count]) => {
                  const totalUsers = Array.from(geographicDistribution.values()).reduce((sum, val) => sum + val, 0);
                  const percentage = totalUsers > 0 ? (count / totalUsers) * 100 : 0;

                  return (
                    <div key={country} className={`p-3 rounded-lg transition-colors ${isDark ? 'bg-gray-700/50 border border-gray-600/50 hover:bg-gray-700' : 'bg-white hover:bg-gray-50'
                      }`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className={`text-sm font-light ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {getTopoCountryDisplayName(country) || country || 'Unknown'}
                          </span>
                        </div>
                        <div className="flex items-center space-x-3">
                          <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            {percentage.toFixed(1)}%
                          </span>
                          <span className={`text-sm font-light ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            {count} users
                          </span>
                        </div>
                      </div>
                      <div className={`w-full h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'
                        }`}>
                        <div
                          className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full transition-all duration-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

      </div>
    </div>
  );

  const renderUsersTab = () => {
    const duplicateEmailCounts = new Map<string, number>();
    users.forEach((user) => {
      const email = (user.email || '').trim().toLowerCase();
      if (!email || email === 'no email') return;
      duplicateEmailCounts.set(email, (duplicateEmailCounts.get(email) || 0) + 1);
    });

    // Separate active and blocked users
    const activeUsers = users.filter(user => user.status !== 'blocked');
    const blockedUsers = users.filter(user => user.status === 'blocked');

    // Get online users from active sessions
    const onlineUserIds = new Set(activeSessions.map(session => session.userId));
    const onlineUsers = users.filter(user => onlineUserIds.has(user.id));

    // Get users based on selected tab
    const getUsersForTab = () => {
      switch (userTab) {
        case 'online':
          return onlineUsers;
        case 'blocked':
          return blockedUsers;
        default:
          return activeUsers;
      }
    };

    // Filter users based on local search term
    const filteredUsers = getUsersForTab().filter(user => {
      if (!userSearchTerm) return true;
      const searchLower = userSearchTerm.toLowerCase();
      const firstName = user.firstName?.toLowerCase() || '';
      const lastName = user.lastName?.toLowerCase() || '';
      const fullName = `${firstName} ${lastName}`.trim();
      const displayName = user.displayName?.toLowerCase() || '';
      const email = user.email?.toLowerCase() || '';
      const userId = user.id.toLowerCase();

      return (
        fullName.includes(searchLower) ||
        displayName.includes(searchLower) ||
        email.includes(searchLower) ||
        userId.includes(searchLower)
      );
    });

    const isUserSelectable = (user: AdminUser) =>
      user.id !== currentUser?.uid && !(user.email && isAdminEmail(user.email));

    const selectableFiltered = filteredUsers.filter(isUserSelectable);
    const selectedCount = selectableFiltered.filter((u) => selectedUserIds.has(u.id)).length;
    const allSelectableSelected =
      selectableFiltered.length > 0 &&
      selectableFiltered.every((u) => selectedUserIds.has(u.id));

    const toggleSelectAll = () => {
      setSelectedUserIds((prev) => {
        const next = new Set(prev);
        if (allSelectableSelected) {
          selectableFiltered.forEach((u) => next.delete(u.id));
        } else {
          selectableFiltered.forEach((u) => next.add(u.id));
        }
        return next;
      });
    };

    const toggleUserSelected = (userId: string) => {
      setSelectedUserIds((prev) => {
        const next = new Set(prev);
        if (next.has(userId)) next.delete(userId);
        else next.add(userId);
        return next;
      });
    };

    return (
      <div className="space-y-6">
        {dataError && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
            <p className="font-bold">Error Loading Users</p>
            <p>{dataError}</p>
          </div>
        )}
        {/* Toggle Section */}
        <div className={`rounded-lg shadow p-6 ${isDark
          ? 'bg-gray-900 border border-gray-700'
          : 'bg-white'
          }`}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div>
                <h3 className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {userTab === 'blocked' ? 'Blocked Users' : userTab === 'online' ? 'Online Users' : 'All Users'}
                </h3>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  {users.length} profiles in Firestore · includes duplicate emails
                </p>
              </div>
              <div className={`flex rounded-lg p-1 ${isDark ? 'bg-gray-800' : 'bg-gray-100'
                }`}>
                <button
                  onClick={() => setUserTab('active')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${userTab === 'active'
                    ? isDark
                      ? 'bg-gray-700 text-white shadow-sm'
                      : 'bg-white text-gray-900 shadow-sm'
                    : isDark
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Active ({activeUsers.length})
                </button>
                <button
                  onClick={() => setUserTab('online')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 flex items-center gap-1.5 ${userTab === 'online'
                    ? isDark
                      ? 'bg-gray-700 text-white shadow-sm'
                      : 'bg-white text-gray-900 shadow-sm'
                    : isDark
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                  Online ({onlineUsers.length})
                </button>
                <button
                  onClick={() => setUserTab('blocked')}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${userTab === 'blocked'
                    ? isDark
                      ? 'bg-gray-700 text-white shadow-sm'
                      : 'bg-white text-gray-900 shadow-sm'
                    : isDark
                      ? 'text-gray-400 hover:text-gray-200'
                      : 'text-gray-500 hover:text-gray-700'
                    }`}
                >
                  Blocked ({blockedUsers.length})
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'
                }`}>
                {filteredUsers.length} of {userTab === 'blocked' ? blockedUsers.length : userTab === 'online' ? onlineUsers.length : activeUsers.length} users
              </div>
              {selectedCount > 0 && (
                <button
                  type="button"
                  disabled={bulkDeleting}
                  onClick={() => handleBulkDeleteUsers(Array.from(selectedUserIds))}
                  className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${isDark
                    ? 'text-red-300 bg-red-900/30 border border-red-800 hover:bg-red-900/50'
                    : 'text-red-700 bg-red-50 border border-red-200 hover:bg-red-100'
                    }`}
                >
                  {bulkDeleting
                    ? bulkDeleteProgress
                      ? `Deleting ${bulkDeleteProgress.current}/${bulkDeleteProgress.total}…`
                      : `Deleting ${selectedCount}…`
                    : `Delete selected (${selectedCount})`}
                </button>
              )}
              <button
                onClick={handleCleanupDuplicates}
                className={`px-3 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${isDark
                  ? 'text-orange-400 bg-orange-900/20 border border-orange-800 hover:bg-orange-900/30 hover:text-orange-300'
                  : 'text-orange-600 bg-orange-50 border border-orange-200 hover:bg-orange-100 hover:text-orange-700'
                  }`}
              >
                Clean Duplicates
              </button>
            </div>
          </div>

          {/* Local Search Input for Users */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className={`h-5 w-5 ${isDark ? 'text-gray-500' : 'text-gray-400'
                }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search users by name, email, or user ID..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              className={`block w-full pl-10 pr-3 py-2 border rounded-md leading-5 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 sm:text-sm ${isDark
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:placeholder-gray-300'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:placeholder-gray-400'
                }`}
            />
            {userSearchTerm && (
              <button
                onClick={() => setUserSearchTerm('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <svg className={`h-5 w-5 ${isDark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'
                  }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Users Table */}
        <div className={`rounded-lg shadow ${isDark
          ? 'bg-gray-900 border border-gray-700'
          : 'bg-white'
          }`}>
          <div className="overflow-x-auto">
            <table className={`min-w-full divide-y ${isDark ? 'divide-gray-700' : 'divide-gray-200'
              }`}>
              <thead className={isDark ? 'bg-gray-800' : 'bg-gray-50'}>
                <tr>
                  <th className={`px-4 py-3 text-left w-12 ${isDark ? 'text-gray-300' : 'text-gray-500'}`}>
                    <input
                      type="checkbox"
                      checked={allSelectableSelected}
                      disabled={selectableFiltered.length === 0 || bulkDeleting}
                      onChange={toggleSelectAll}
                      className="rounded border-gray-400 text-cyan-600 focus:ring-cyan-500"
                      title="Select all visible users"
                      aria-label="Select all visible users"
                    />
                  </th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}>User</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}>Email</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}>User ID</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}>Subscription</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}>Status</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}>Created</th>
                  <th className={`px-6 py-3 text-left text-xs font-medium uppercase tracking-wider ${isDark ? 'text-gray-300' : 'text-gray-500'
                    }`}>Actions</th>
                </tr>
              </thead>
              <tbody className={`divide-y ${isDark ? 'bg-gray-900 divide-gray-700' : 'bg-white divide-gray-200'
                }`}>
                {filteredUsers.length > 0 ? (
                  filteredUsers.map((user) => {
                    const selectable = isUserSelectable(user);
                    const isSelected = selectedUserIds.has(user.id);
                    return (
                    <tr
                      key={user.id}
                      className={`${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50'} ${isSelected ? (isDark ? 'bg-red-900/10' : 'bg-red-50/50') : ''}`}
                    >
                      <td className="px-4 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          disabled={!selectable || bulkDeleting}
                          onChange={() => toggleUserSelected(user.id)}
                          className="rounded border-gray-400 text-cyan-600 focus:ring-cyan-500 disabled:opacity-40"
                          title={
                            !selectable
                              ? user.id === currentUser?.uid
                                ? 'Cannot delete your own account'
                                : 'Admin accounts cannot be bulk-deleted'
                              : 'Select for bulk delete'
                          }
                          aria-label={`Select ${user.email || user.id}`}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {user.email?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-3">
                            <button
                              onClick={() => handleUserClick(user)}
                              className={`text-sm font-medium hover:underline transition-colors duration-200 ${isDark
                                ? 'text-white hover:text-cyan-300'
                                : 'text-gray-900 hover:text-cyan-600'
                                }`}
                            >
                              {user.firstName} {user.lastName}
                            </button>
                            {user.signupSource && (
                              <span
                                className="ml-2 inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded-full text-white"
                                style={{ backgroundColor: user.signupSourceBadgeColor || '#3b82f6' }}
                              >
                                {user.signupSourceBadge || '🚀 Pilot'}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'
                        }`}>
                        <button
                          onClick={() => handleUserClick(user)}
                          className={`hover:underline transition-colors duration-200 ${isDark
                            ? 'text-gray-300 hover:text-cyan-300'
                            : 'text-gray-500 hover:text-cyan-600'
                            }`}
                        >
                          {user.email}
                        </button>
                        {(() => {
                          const emailKey = (user.email || '').trim().toLowerCase();
                          if (!emailKey || emailKey === 'no email') return null;
                          if ((duplicateEmailCounts.get(emailKey) || 0) < 2) return null;
                          return (
                            <span
                              className={`ml-2 inline-flex px-1.5 py-0.5 text-[10px] font-semibold rounded ${isDark
                                ? 'bg-orange-900/40 text-orange-300'
                                : 'bg-orange-100 text-orange-700'
                                }`}
                              title="Same email exists on another user document"
                            >
                              duplicate email
                            </span>
                          );
                        })()}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-mono text-xs ${isDark ? 'text-gray-300' : 'text-gray-500'
                        }`}>
                        <button
                          onClick={() => handleUserClick(user)}
                          className={`hover:underline transition-colors duration-200 ${isDark
                            ? 'text-gray-300 hover:text-cyan-300'
                            : 'text-gray-500 hover:text-cyan-600'
                            }`}
                        >
                          {user.id}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {(() => {
                            const rawPlan = String(user.subscription?.plan || 'free').toLowerCase();
                            const plan =
                              rawPlan === 'enterprise'
                                ? 'enterprise'
                                : rawPlan === 'business'
                                  ? 'business'
                                  : rawPlan === 'pro'
                                    ? 'pro'
                                    : 'free';
                            const badgeClass = isDark
                              ? plan === 'enterprise'
                                ? 'bg-amber-900/30 text-amber-200'
                                : plan === 'business'
                                  ? 'bg-violet-900/30 text-violet-200'
                                  : plan === 'pro'
                                    ? 'bg-purple-900/20 text-purple-300'
                                    : 'bg-gray-800 text-gray-300'
                              : plan === 'enterprise'
                                ? 'bg-amber-100 text-amber-800'
                                : plan === 'business'
                                  ? 'bg-violet-100 text-violet-800'
                                  : plan === 'pro'
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-gray-100 text-gray-800';
                            const label =
                              plan === 'enterprise'
                                ? 'Enterprise'
                                : plan === 'business'
                                  ? 'Business'
                                  : plan === 'pro'
                                    ? 'Pro'
                                    : 'Free';
                            return (
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${isDark
                            ? badgeClass
                            : badgeClass
                            }`}>
                            {label}
                          </span>
                            );
                          })()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${isDark
                          ? user.status === 'active' ? 'bg-green-900/20 text-green-300' :
                            user.status === 'blocked' ? 'bg-red-900/20 text-red-300' :
                              'bg-gray-800 text-gray-300'
                          : user.status === 'active' ? 'bg-green-100 text-green-800' :
                            user.status === 'blocked' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                          }`}>
                          {user.status === 'active' ? 'Active' :
                            user.status === 'blocked' ? 'Blocked' : 'Inactive'}
                        </span>
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-gray-300' : 'text-gray-500'
                        }`}>
                        {new Date(user.createdAt.seconds * 1000).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleUserClick(user)}
                            className={`transition-colors duration-200 ${isDark
                              ? 'text-cyan-400 hover:text-cyan-300'
                              : 'text-cyan-600 hover:text-cyan-900'
                              }`}
                          >
                            View Details
                          </button>
                          {userTab !== 'blocked' && user.status !== 'blocked' && (
                            <button
                              onClick={() => setBlockConfirmation({ user, isOpen: true })}
                              className={`transition-colors duration-200 ${isDark
                                ? 'text-red-400 hover:text-red-300'
                                : 'text-red-600 hover:text-red-900'
                                }`}
                            >
                              Block User
                            </button>
                          )}
                          {userTab === 'blocked' && user.status === 'blocked' && (
                            <button
                              onClick={() => handleUnblockUser(user)}
                              className={`transition-colors duration-200 ${isDark
                                ? 'text-green-400 hover:text-green-300'
                                : 'text-green-600 hover:text-green-900'
                                }`}
                            >
                              Unblock User
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                  })
                ) : (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center">
                        <svg className={`mx-auto h-12 w-12 ${isDark ? 'text-gray-500' : 'text-gray-400'
                          }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'
                          }`}>No users found</h3>
                        <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'
                          }`}>
                          {userSearchTerm ? `No users match "${userSearchTerm}"` : 'No users available'}
                        </p>
                        {userSearchTerm && (
                          <button
                            onClick={() => setUserSearchTerm('')}
                            className={`mt-2 text-sm transition-colors duration-200 ${isDark
                              ? 'text-cyan-400 hover:text-cyan-300'
                              : 'text-cyan-600 hover:text-cyan-900'
                              }`}
                          >
                            Clear search
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };


  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'users':
        return renderUsersTab();
      case 'campaigns':
        return <AdminCampaignsPage />;
      case 'email-templates':
        return <AdminEmailTemplateManager />;
      case 'signup-forms':
        return <AdminSignupFormManager />;
      case 'automation-templates':
        return <AdminAutomationTemplateManager />;
      case 'automation-control':
        return <AdminAutomationControl />;
      case 'automations':
        return <AdminMailAutomationHub />;
      case 'dns-checker':
        return <StandaloneDNSChecker />;
      case 'templates':
        return <AdminTemplateManager />;
      case 'video-tutorials':
        return <AdminVideoTutorialManager />;
      case 'blog-posts':
        return <AdminBlogManager />;
      case 'news-center':
        return <AdminNewsManager />;
      case 'app-offer-banners':
        return <AdminAppOfferBannerManager />;
      case 'help-center':
        return <AdminHelpCenterManager />;
      case 'analytics':
        return <AdminAnalyticsDashboard />;
      case 'revenue':
        return <AdminRevenuePage />;
      case 'current-subscription':
        return <AdminCurrentSubscriptionsPage />;
      case 'cancelled-subscription':
        return <AdminCancelledSubscriptionsPage />;
      case 'transaction':
        return <AdminDodoTransactionsPage />;
      case 'system':
        return <div className="text-center py-12"><p className="text-gray-500">System control panel coming soon...</p></div>;
      case 'notifications':
        return <AdminNotificationPage />;
      case 'plan':
        return <AdminPlanPricing />;
      case 'settings':
        return <AdminSettings />;
      case 'email':
        return <AdminSystemEmails />;
      case 'system-emails':
        return <AdminMailAutomationHub defaultFolder="system" />;
      case 'status':
        return <AdminStatusPageManager />;
      case 'feedback':
        return <AdminFeedbackManager />;
      case 'contact-submissions':
        return <AdminContactSubmissionsPage />;
      case 'marketing':
        return <AdminMarketingPage />;
      case 'api':
        return <AdminApiManager />;
      case 'api-activity':
        return <AdminApiActivity />;
      case 'sender':
        return <AdminSenderManager />;
      case 'email-service':
        return <AdminEmailService />;
      case 'system-secrets':
        return <AdminSecretsManager />;
      case 'firebase-database':
        return <AdminFirebaseDatabase />;
      case 'uploads':
        return <AdminUploads />;
      default:
        return renderOverviewTab();
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  return (
    <div className={`h-screen flex ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      <AdminNotificationListener />
      {/* Sidebar */}
      <AdminSidebar
        activeTab={activeTab}
        onNotificationClick={() => setIsNotificationCenterOpen(true)}
        alertCount={alerts.length}
        isDark={isDark}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden bg-white">
        {/* Premium Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            {/* Left: Title */}
            <div className="flex items-center space-x-4">
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  {activeTab === 'overview' ? 'Sales Overview' :
                    activeTab === 'users' ? 'User Management' :
                      activeTab === 'campaigns' ? 'Campaigns' :
                      activeTab === 'email' ? 'Email Logs' :
                      activeTab === 'uploads' ? 'User Uploads' :
                      activeTab === 'system-emails' ? 'System Emails' :
                        activeTab === 'email-templates' ? 'Email Templates' :
                          activeTab === 'signup-forms' ? 'Signup Forms' :
                            activeTab === 'automation-templates' ? 'Automation Templates' :
                              activeTab === 'automation-control' ? 'Automation Control' :
                              activeTab === 'automations' ? 'Send Email' :
                                  activeTab === 'dns-checker' ? 'DNS Checker' :
                                    activeTab === 'api' ? 'API Management' :
                                      activeTab === 'api-activity' ? 'API Activity' :
                                      activeTab === 'templates' ? 'Templates' :
                                        activeTab === 'video-tutorials' ? 'Video Tutorials' :
                                          activeTab === 'blog-posts' ? 'Blog Posts' :
                                            activeTab === 'news-center' ? 'News Center' :
                                              activeTab === 'app-offer-banners' ? 'Offer Banners' :
                                              activeTab === 'help-center' ? 'Help Center' :
                                                activeTab === 'analytics' ? 'Analytics' :
                                                  activeTab === 'revenue' ? 'Revenue' :
                                                    activeTab === 'current-subscription' ? 'Current Subscription' :
                                                      activeTab === 'cancelled-subscription' ? 'Cancelled Subscription' :
                                                        activeTab === 'transaction' ? 'Transaction' :
                                                    activeTab === 'system' ? 'System' :
                                                      activeTab === 'notifications' ? 'Notifications' :
                                                        activeTab === 'plan' ? 'Plan Pricing' :
                                                          activeTab === 'status' ? 'Status Page' :
                                                            activeTab === 'feedback' ? 'Feedback' :
                                                              activeTab === 'contact-submissions' ? 'Contact Submissions' :
                                                            activeTab === 'settings' ? 'Settings' :
                                                              activeTab === 'email-service' ? 'Email Service' :
                                                                activeTab === 'system-secrets' ? 'System Secrets' :
                                                                  activeTab === 'firebase-database' ? 'Firebase Database' :
                                                                    activeTab === 'marketing' ? 'Marketing' : 'Dashboard'}
                </h1>
                <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Discover</p>
              </div>
            </div>

            {/* Center: Search Bar */}
            <div className="flex-1 max-w-md mx-8 relative" ref={searchRef}>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search anything (users, campaigns, API keys)..."
                  value={searchTerm}
                  onFocus={() => setIsSearchDropdownOpen(true)}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setIsSearchDropdownOpen(true);
                  }}
                  className={`block w-full pl-10 pr-10 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 ${isDark
                    ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400 shadow-lg shadow-black/10'
                    : 'border-gray-300 bg-gray-50 text-gray-900 placeholder-gray-400 shadow-sm'
                    }`}
                />
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm('');
                      setIsSearchDropdownOpen(false);
                    }}
                    className={`absolute inset-y-0 right-0 pr-3 flex items-center transition-colors ${isDark
                      ? 'text-gray-500 hover:text-gray-300'
                      : 'text-gray-400 hover:text-gray-600'
                      }`}
                    title="Clear search"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Search Results Dropdown Overlay */}
              {isSearchDropdownOpen && searchTerm.trim() && (
                <div className={`absolute top-full left-0 right-0 mt-3 rounded-2xl shadow-2xl border backdrop-blur-xl overflow-hidden z-[100] transition-all duration-200 animate-in fade-in slide-in-from-top-2 ${isDark ? 'bg-gray-800/95 border-gray-700' : 'bg-white/95 border-gray-200'
                  }`}>
                  <div className="max-h-[70vh] overflow-y-auto custom-scrollbar">
                    {/* Users Section */}
                    {filteredResults.users.length > 0 && (
                      <div className="p-2">
                        <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">Users</div>
                        {filteredResults.users.map(user => (
                          <div
                            key={user.id}
                            onClick={() => handleSearchResultClick('user', user)}
                            className={`flex items-center space-x-3 px-3 py-2 rounded-xl cursor-pointer transition-all duration-200 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                              }`}
                          >
                            <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-bold">
                              {user.email[0].toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{user.displayName || `${user.firstName || ''} ${user.lastName || ''}` || 'Unknown'}</div>
                              <div className="text-xs text-gray-500 truncate">{user.email}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Campaigns Section */}
                    {filteredResults.campaigns.length > 0 && (
                      <div className="p-2 border-t border-gray-700/50">
                        <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">Campaigns</div>
                        {filteredResults.campaigns.map(campaign => (
                          <div
                            key={campaign.id}
                            onClick={() => handleSearchResultClick('campaign', campaign)}
                            className={`flex items-center space-x-3 px-3 py-2 rounded-xl cursor-pointer transition-all duration-200 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                              }`}
                          >
                            <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 text-xs font-bold">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{campaign.name}</div>
                              <div className="text-xs text-gray-500 truncate">Workspace: {campaign.workspaceId || 'Default'}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* API Keys Section */}
                    {filteredResults.apiKeys.length > 0 && (
                      <div className="p-2 border-t border-gray-700/50">
                        <div className="px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-gray-400">API Access Tokens</div>
                        {filteredResults.apiKeys.map(key => (
                          <div
                            key={key.id}
                            onClick={() => handleSearchResultClick('api', key)}
                            className={`flex items-center space-x-3 px-3 py-2 rounded-xl cursor-pointer transition-all duration-200 ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                              }`}
                          >
                            <div className="w-8 h-8 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center text-cyan-600 dark:text-cyan-400 text-xs font-bold">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                              </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className={`text-sm font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{key.name}</div>
                              <div className="text-xs text-gray-500 truncate">ID: {key.id}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* No Results */}
                    {filteredResults.users.length === 0 && filteredResults.campaigns.length === 0 && filteredResults.apiKeys.length === 0 && (
                      <div className="p-8 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 mb-3">
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                          </svg>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          No results found for "<span className="font-semibold">{searchTerm}</span>"
                        </p>
                      </div>
                    )}
                  </div>
                  <div className={`px-4 py-3 text-[10px] italic border-t ${isDark ? 'bg-gray-900/50 border-gray-700 text-gray-500' : 'bg-gray-50 border-gray-100 text-gray-400'}`}>
                    Press Esc to close • Result limits apply for performance
                  </div>
                </div>
              )}
            </div>

            {/* Right: Actions and User */}
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <button
                onClick={() => setIsNotificationCenterOpen(true)}
                className={`relative p-2 rounded-lg transition-colors ${isDark
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {alerts.length > 0 && (
                  <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500"></span>
                )}
              </button>

              {/* Theme Toggle */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('Theme toggle button clicked, current theme:', isDark ? 'dark' : 'light');
                  toggleTheme();
                }}
                className={`p-2 rounded-lg transition-colors ${isDark
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
                aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
                type="button"
              >
                {isDark ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Help */}
              <button className={`p-2 rounded-lg transition-colors ${isDark
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>

              {/* User Profile */}
              <div className={`flex items-center space-x-2 pl-4 border-l ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
                  {currentUser?.email?.charAt(0).toUpperCase() || 'A'}
                </div>
                <div className="hidden md:block">
                  <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{currentUser?.email?.split('@')[0] || 'Admin'}</p>
                  <p className="text-xs text-gray-500">{currentUser?.email || 'admin@scubamail.com'}</p>
                </div>
                <button className="text-gray-400 hover:text-gray-600">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto p-6 bg-gray-50">
          {renderTabContent()}
        </div>
      </div>

      {/* User Detail Modal */}
      {isUserModalOpen && selectedUser && (
        <UserDetailModal
          user={selectedUser}
          isOpen={isUserModalOpen}
          onClose={() => {
            setIsUserModalOpen(false);
            setSelectedUser(null);
          }}
        />
      )}

      {/* Block Confirmation Modal */}
      {blockConfirmation.isOpen && blockConfirmation.user && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg p-6 max-w-md w-full mx-4 ${isDark ? 'bg-gray-800' : 'bg-white'
            }`}>
            <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'
              }`}>
              Block User
            </h3>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'
              }`}>
              Are you sure you want to block {blockConfirmation.user.firstName} {blockConfirmation.user.lastName}?
              This will sign them out and prevent them from accessing the system.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setBlockConfirmation({ user: null, isOpen: false })}
                className={`px-4 py-2 rounded-md transition-colors duration-200 ${isDark
                  ? 'bg-gray-600 hover:bg-gray-500 text-white'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                  }`}
              >
                Cancel
              </button>
              <button
                onClick={() => blockConfirmation.user && handleBlockUser(blockConfirmation.user)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors duration-200"
              >
                Block User
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboardLayout;
